import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import {
  CARTELLA_STORAGE,
  COLLEZIONE_APPARECCHI,
  type ApparecchioAr,
  type RiferimentoAr,
  type SegnoAr,
  type SuperficieAr,
} from '../types/ApparecchiArTypes';

/**
 * Legge e scrive il catalogo degli apparecchi riconoscibili in realtà aumentata.
 *
 * ⚠️ **La console non crea apparecchi: li rifinisce.** Le fotografie si fanno dove sta
 * l'apparecchio, con l'app in mano e ARCore che giudica lo scatto sul posto — non c'è
 * modo di farle da qui. Quello che qui si fa meglio è nominare le parti e mettere i
 * punti al pixel giusto, che su uno schermo da sei pollici viene male.
 */

/** Tutti gli apparecchi, i più recenti per primi. */
export async function elencaApparecchi(): Promise<ApparecchioAr[]> {
  const q = query(collection(db, COLLEZIONE_APPARECCHI), orderBy('aggiornato_il', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...(d.data() as ApparecchioAr), id: d.id }));
}

export async function leggiApparecchio(id: string): Promise<ApparecchioAr | null> {
  const snap = await getDoc(doc(db, COLLEZIONE_APPARECCHI, id));
  if (!snap.exists()) return null;
  return { ...(snap.data() as ApparecchioAr), id: snap.id };
}

/**
 * Risolve gli URL di lettura delle fotografie.
 *
 * ⚠️ Il documento porta il PERCORSO su Storage, non un URL: gli URL firmati scadono, e
 * un documento che ne conservasse uno diventerebbe silenziosamente inutile dopo qualche
 * tempo. Si risolvono a ogni apertura.
 */
export async function risolviImmagini(a: ApparecchioAr): Promise<ApparecchioAr> {
  const superfici = await Promise.all(
    a.superfici.map(async (s) => {
      const url = await getDownloadURL(ref(storage, s.immagine)).catch(() => undefined);
      return { ...s, immagine_url: url };
    }),
  );
  return { ...a, superfici };
}

/** Salva i segni di una superficie. Tocca solo quella: le altre restano com'erano. */
export async function salvaSegni(
  apparecchioId: string,
  superficie: SuperficieAr,
  segni: SegnoAr[],
  tutte: SuperficieAr[],
): Promise<void> {
  const aggiornate = tutte.map((s) =>
    s.id === superficie.id
      ? // ⚠️ `immagine_url` non si scrive: è una risoluzione temporanea, non un dato.
        // Salvarla congelerebbe un URL destinato a scadere dentro il documento.
        { ...stripUrl(s), segni }
      : stripUrl(s),
  );
  await updateDoc(doc(db, COLLEZIONE_APPARECCHI, apparecchioId), {
    superfici: aggiornate,
    aggiornato_il: serverTimestamp(),
    aggiornato_da: auth.currentUser?.email ?? 'console',
  });
}

/** Salva l'anagrafica: parti, domande, dimensioni. */
export async function salvaAnagrafica(
  apparecchioId: string,
  campi: Partial<
    Pick<
      ApparecchioAr,
      | 'marca'
      | 'modello'
      | 'descrizione'
      | 'codici_modello'
      | 'larghezza_mm'
      | 'altezza_mm'
      | 'profondita_mm'
      | 'parti'
      | 'domande'
    >
  >,
): Promise<void> {
  await updateDoc(doc(db, COLLEZIONE_APPARECCHI, apparecchioId), {
    ...campi,
    aggiornato_il: serverTimestamp(),
    aggiornato_da: auth.currentUser?.email ?? 'console',
  });
}

/**
 * Mette o toglie un apparecchio dalla circolazione.
 *
 * ⚠️ **Un apparecchio senza segni non si dovrebbe pubblicare**, e chi preme il comando
 * deve saperlo: riconosce l'apparecchio e non sa indicare niente, che a un installatore
 * in cantiere sembra un guasto. Il controllo sta in schermata, non qui — questa
 * funzione fa quello che le si chiede.
 */
export async function pubblica(apparecchioId: string, acceso: boolean): Promise<void> {
  await updateDoc(doc(db, COLLEZIONE_APPARECCHI, apparecchioId), {
    pubblicato: acceso,
    aggiornato_il: serverTimestamp(),
    aggiornato_da: auth.currentUser?.email ?? 'console',
  });
}

/**
 * Carica un documento di consultazione.
 *
 * ⚠️ Sta in una sottocartella `riferimenti/`, separata dalle fotografie di bersaglio.
 * Mescolarli renderebbe possibile che un manuale finisca per sbaglio nell'archivio di
 * ARCore come superficie da riconoscere: il sistema proverebbe ad agganciare una pagina
 * stampata su un inverter, e fallirebbe senza dire perché.
 */
export async function caricaRiferimento(
  apparecchioId: string,
  file: File,
  descrizione: string,
  esistenti: RiferimentoAr[],
  gruppo?: string,
  ordine?: number,
): Promise<RiferimentoAr> {
  /*
    ⚠️ L'identificativo porta un pezzo casuale, non il conteggio degli esistenti:
    caricando più file insieme il conteggio è lo stesso per tutti, e i file si
    sovrascrivono a vicenda su Storage — l'ultimo vince e gli altri spariscono senza
    che niente fallisca.
  */
  const impronta = Math.random().toString(36).slice(2, 8);
  const id = `rif-${impronta}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const percorso = `${CARTELLA_STORAGE}/${apparecchioId}/riferimenti/${id}`;
  await uploadBytes(ref(storage, percorso), file, { contentType: file.type });
  const nuovo: RiferimentoAr = {
    id,
    nome: file.name,
    file: percorso,
    tipo: file.type,
    descrizione,
    byte: file.size,
    ...(gruppo ? { gruppo } : {}),
    ...(ordine !== undefined ? { ordine } : {}),
  };
  await updateDoc(doc(db, COLLEZIONE_APPARECCHI, apparecchioId), {
    riferimenti: [...esistenti, nuovo],
    aggiornato_il: serverTimestamp(),
    aggiornato_da: auth.currentUser?.email ?? 'console',
  });
  return nuovo;
}

export async function togliRiferimento(
  apparecchioId: string,
  r: RiferimentoAr,
  esistenti: RiferimentoAr[],
): Promise<void> {
  // ⚠️ Prima il documento, poi il file: se si cancellasse prima il file e la scrittura
  // fallisse, resterebbe un riferimento che nomina qualcosa che non c'è più.
  await updateDoc(doc(db, COLLEZIONE_APPARECCHI, apparecchioId), {
    riferimenti: esistenti.filter((x) => x.id !== r.id),
    aggiornato_il: serverTimestamp(),
    aggiornato_da: auth.currentUser?.email ?? 'console',
  });
  await deleteObject(ref(storage, r.file)).catch(() => undefined);
}

function stripUrl(s: SuperficieAr): SuperficieAr {
  const { immagine_url: _scartato, ...resto } = s;
  return resto as SuperficieAr;
}

/** Quanti punti ha una superficie, e quanti sono ancora bozze da confermare. */
export function conteggio(s: SuperficieAr): { posati: number; bozze: number } {
  const bozze = s.segni.filter((g) => g.origine === 'bozza_ia').length;
  return { posati: s.segni.length - bozze, bozze };
}
